import { FC, useContext, useEffect, useState } from 'react';
import { get } from 'lodash-es';
import { useOtherSettings } from '@namespace/cms';
import { useDeviceStore } from '@namespace/device';
import {
  FORM_COMPONENTS_TYPES,
  FORM_VALIDATION_TYPES,
  FormBuilder,
  useFormMethods
} from '@namespace/forms';
import { useI18n } from '@namespace/i18n';
import { UserContext } from '@namespace/user/src/context';
import { CUSTOM_AMOUNT_OPTION, CUSTOM_SESSION_DURATION } from '../../constants';
import { SaveLimitsArgs } from '../GamingActivity/types';
import useLimitPresets from './hooks/useLimitPresets';
import { getDynamicValues } from './utils/getDynamicValues';
import { getPopoverTextByPeriod } from './utils/getPopoverTextByPeriod';
import { Configuration } from './types';

import styles from './index.module.css';

interface IProps {
  onSubmit: (data: SaveLimitsArgs) => void;
  inputSize?: string;
  buttonSize?: string;
  currencyClassName?: string;
  isShowTooltipText?: boolean;
  isNextButton?: boolean;
}

const SetLimitForm: FC<IProps> = ({
  onSubmit,
  inputSize = 's',
  buttonSize = 'l',
  currencyClassName = '',
  isShowTooltipText = false,
  isNextButton = false
}) => {
  const [user] = useContext(UserContext);
  const { defaultCurrency: currency, mgaCurrency, limits } = user;
  const { isMobile } = useDeviceStore();
  const { t } = useI18n();

  const gameActivityLimit = get(limits, 'gameActivity[0]', null);
  const maxDepositLimit = get(limits, 'maxDeposit[0]', null);
  const [isEdit, setIsEdit] = useState(
    Boolean(gameActivityLimit && maxDepositLimit)
  );
  const [isSavedLimitsPeriodChanged, setIsSavedLimitsPeriodChanged] =
    useState(false);
  const [isCurrentPeriodEqualSavedPeriod, setIsCurrentPeriodEqualSavedPeriod] =
    useState(false);

  useEffect(() => {
    setIsEdit(Boolean(gameActivityLimit && maxDepositLimit));
  }, [gameActivityLimit, maxDepositLimit]);

  const EDITING_DISABLED =
    maxDepositLimit &&
    gameActivityLimit &&
    gameActivityLimit.changeAllowedFrom &&
    new Date(gameActivityLimit.changeAllowedFrom * 1000).getTime() >
      new Date().getTime();

  const { uaRegulation = {} } = useOtherSettings();

  const { requestValue: defaultPeriod } =
    (uaRegulation.limits || []).find((i: Configuration) => i.default) ?? {};
  const defaultValues = { period: defaultPeriod };
  const formMethods = useFormMethods({ defaultValues });

  const { watch } = formMethods;
  const limit = watch('limit');
  const duration = watch('duration');
  const period = watch('period');

  const {
    defaultConfig: { depositLimit, durationLimit },
    options
  } = useLimitPresets(period);

  const dynamicValues = getDynamicValues({
    isEdit,
    depositLimit,
    durationLimit,
    gameActivityLimit,
    maxDepositLimit,
    options,
    isSavedLimitsPeriodChanged
  });

  useEffect(() => {
    if (!gameActivityLimit?.period || !maxDepositLimit?.period || isEdit) {
      return;
    }

    const isPeriodsAreEqual =
      period === gameActivityLimit.period && period === maxDepositLimit.period;

    if (isPeriodsAreEqual) {
      setIsCurrentPeriodEqualSavedPeriod(true);
      return;
    }

    if (isCurrentPeriodEqualSavedPeriod) {
      setIsSavedLimitsPeriodChanged(true);
    }
  }, [
    period,
    gameActivityLimit,
    maxDepositLimit,
    isEdit,
    isCurrentPeriodEqualSavedPeriod
  ]);

  const components = [
    {
      type: FORM_COMPONENTS_TYPES.SELECT,
      name: 'period',
      size: inputSize,
      label:
        'web.responsiblegambling.simplifiedinterface.depositlimits.period.label',
      direction: isMobile ? 'column' : 'row',
      options: options.period,
      disabled: isEdit,
      className: styles.wrapper,
      wrapperPopoverStyle: styles.wrapperPopover,
      ...(isShowTooltipText
        ? {
            tooltipText: [
              t('web.responsiblegambling.gamingactivity.form.period.hint')
            ]
          }
        : {})
    },
    {
      type: FORM_COMPONENTS_TYPES.SELECT,
      name: 'limit',
      size: inputSize,
      label:
        'web.responsiblegambling.simplifiedinterface.depositlimits.amount.label',
      direction: isMobile ? 'column' : 'row',
      options: options.limit,
      disabled: isEdit,
      className: styles.wrapper,
      wrapperPopoverStyle: styles.wrapperPopover,
      ...(isShowTooltipText
        ? {
            tooltipText: getPopoverTextByPeriod(options, 'limit', period, t)
          }
        : {})
    },
    limit === CUSTOM_AMOUNT_OPTION && {
      type: FORM_COMPONENTS_TYPES.AMOUNT_INPUT_FIELD,
      currency: mgaCurrency ?? currency,
      placeholder: t(
        'web.responsiblegambling.simplifiedinterface.depositlimits.custom.placeholder'
      ),
      size: inputSize,
      label:
        'web.responsiblegambling.simplifiedinterface.depositlimits.custom.label',
      name: 'amount',
      validationTypes: [
        FORM_VALIDATION_TYPES.REQUIRED,
        FORM_VALIDATION_TYPES.NUMBER_FORMAT_VALIDATOR,
        {
          type: FORM_VALIDATION_TYPES.MIN_VALIDATOR,
          parameters: [
            depositLimit?.customValue?.min,
            'web.responsiblegambling.simplifiedinterface.depositlimits.amount.error'
          ]
        }
      ],
      classNames: {
        currencyClassName
      },
      isOnChangeValidation: true,
      truncateAfterAmount: 10 ** 15,
      inputType: 'text',
      onlyIntegers: true,
      disabled: isEdit
    },
    {
      type: FORM_COMPONENTS_TYPES.SELECT,
      name: 'duration',
      size: inputSize,
      label:
        'web.responsiblegambling.simplifiedinterface.depositlimits.sessionduration.label',
      direction: isMobile ? 'column' : 'row',
      options: options.duration,
      disabled: isEdit,
      className: styles.wrapper,
      wrapperPopoverStyle: styles.wrapperPopover,
      ...(isShowTooltipText
        ? {
            tooltipText: getPopoverTextByPeriod(options, 'duration', period, t)
          }
        : {})
    },
    duration === CUSTOM_SESSION_DURATION && {
      type: FORM_COMPONENTS_TYPES.AMOUNT_INPUT_FIELD,
      currency: t(
        'web.responsiblegambling.simplifiedinterface.depositlimits.sessionduration.hours1'
      ),
      placeholder: t(
        'web.responsiblegambling.simplifiedinterface.depositlimits.sessionduration.custom.placeholder'
      ),
      size: inputSize,
      label:
        'web.responsiblegambling.simplifiedinterface.depositlimits.sessionduration.custom.label',
      name: 'durationAmount',
      dependsOnField: 'period',
      validationTypes: [
        FORM_VALIDATION_TYPES.REQUIRED,
        FORM_VALIDATION_TYPES.NUMBER_FORMAT_VALIDATOR,
        {
          type: FORM_VALIDATION_TYPES.MIN_VALIDATOR,
          parameters: [
            durationLimit?.customValue?.min,
            'web.responsiblegambling.simplifiedinterface.depositlimits.sessionduration.error1',
            [
              durationLimit?.customValue?.min,
              durationLimit?.customValue?.max,
              { withValue: false }
            ]
          ]
        },
        {
          type: FORM_VALIDATION_TYPES.MAX_VALIDATOR,
          parameters: [
            durationLimit?.customValue?.max,
            'web.responsiblegambling.simplifiedinterface.depositlimits.sessionduration.error1',
            [
              durationLimit?.customValue?.min,
              durationLimit?.customValue?.max,
              { withValue: false }
            ]
          ]
        }
      ],
      classNames: {
        currencyClassName
      },
      isOnChangeValidation: true,
      inputType: 'text',
      onlyIntegers: true,
      disabled: isEdit
    },
    {
      type: FORM_COMPONENTS_TYPES.SUBMIT_BUTTON,
      classNames: { className: styles.submitButton },
      size: buttonSize,
      isNextButton,
      value: t(
        isEdit
          ? 'web.responsiblegambling.simplifiedinterface.editbutton'
          : 'web.responsiblegambling.simplifiedinterface.savebutton'
      ),
      disabled: EDITING_DISABLED
    }
  ];

  const onSubmitButton = (...props: [SaveLimitsArgs]) => {
    if (isEdit) {
      setIsEdit(false);
    } else {
      onSubmit(...props);
      setIsSavedLimitsPeriodChanged(false);
    }
  };

  return (
    <>
      <FormBuilder
        components={components}
        wrapperClassName={styles.form}
        onSubmit={onSubmitButton}
        formConfig={{ mode: 'onSubmit' }}
        formProps={{ dynamicValues }}
        formMethods={formMethods}
      />
    </>
  );
};

export default SetLimitForm;
